import { Location } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { DataService } from '@app/shared/common/auth/data.service';
import { AppConsts } from '@shared/commonHelper/AppConsts';
import { Subject, timer } from 'rxjs';
import { filter, switchMap, take, takeUntil } from 'rxjs/operators';
import { BillingApiService, errorMessageOf } from '../billing-api.service';
import { BillingStateService } from '../billing-state.service';
import { ToastrService } from 'ngx-toastr';
import {
    BILLING_TALK_TO_US_HREF,
    BillingPlanName,
    BillingStatus,
    ChangePlanResult,
    CheckoutStatus,
    formatMoney,
    PlanChangeOptions,
    PlanSelection,
} from '../billing.models';

type GateMode = 'none' | 'upgrade' | 'blocked' | 'confirming' | 'done' | 'slow' | 'changePlan';

/** Settings → Billing → Change plan. */
interface ChangePlanView {
    currentLine: string;
    pendingLine: string;
    reason: string;
    buttonLabel: string;
    buttonDisabled: boolean;
    note: string;
}
type BlockReason = 'trialEnded' | 'paused' | 'ended';

interface GateView {
    mode: GateMode;
    reason: BlockReason | null;
    isAdmin: boolean;
    hasPrices: boolean;
    title: string;
    message: string;
    icon: string;
    iconTone: 'amber' | 'red' | 'grey';
    trialLine: string;
    cardLine: string;
    declinedLine: string;
    dueLine: string;
    ownerName: string;
    ownerInitials: string;
    ownerPicture: string;
    askSent: boolean;
    askSentLabel: string;
    doneSummary: string;
}

const POLL_MS = 2000;
const SLOW_AFTER_MS = 30000;
const BACKGROUND_POLL_MS = 10000;
const BACKGROUND_POLL_LIMIT_MS = 15 * 60 * 1000;

/**
 * The billing pop-ups of "Upgrade and Trial Ended.dc.html", hosted once by the app shell (AppComponent) so they cover every
 * route, the Teams tab included:
 *  - 1a the upgrade pop-up (closable; admins; opened from Upgrade buttons via DataService.openUpgradeModal, and by itself at 3
 *    days and 1 day left),
 *  - 1b / 1c / 1f the blocking pop-up when the firm cannot use Trovve (trial ended, paused, ended): no close, nothing behind
 *    it is usable; admins pay, team members ask the admin,
 *  - 1d "Confirming your payment" after Stripe Checkout: unlocks only once the webhook has recorded the subscription.
 */
@Component({
    selector: 'vf-billing-gate',
    templateUrl: './billing-gate.component.html',
    styleUrls: ['./billing-gate.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BillingGateComponent implements OnInit, OnDestroy {
    readonly talkToUsHref = BILLING_TALK_TO_US_HREF;
    status: BillingStatus | null = null;
    view: GateView = BillingGateComponent.emptyView();
    paying = false;
    asking = false;
    openingPortal = false;
    changing = false;
    changeOptions: PlanChangeOptions | null = null;
    changeView: ChangePlanView = BillingGateComponent.emptyChangeView();

    private selection: PlanSelection = { plan: 'yearly', people: 1 };
    private changeSelection: BillingPlanName | null = null;
    private confirmedCheckout: CheckoutStatus | null = null;
    private readonly destroy$ = new Subject<void>();
    private readonly stopPolling$ = new Subject<void>();

    constructor(
        private api: BillingApiService,
        private billingState: BillingStateService,
        private dataService: DataService,
        private location: Location,
        private toastr: ToastrService,
        private cd: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.readCheckoutReturn();

        this.billingState.status$
            .pipe(
                filter((status): status is BillingStatus => status !== null),
                takeUntil(this.destroy$)
            )
            .subscribe(status => this.onStatus(status));

        this.dataService.openUpgradeModal.pipe(takeUntil(this.destroy$)).subscribe(() => this.openUpgrade());
        this.billingState.changePlanRequests$.pipe(takeUntil(this.destroy$)).subscribe(() => this.openChangePlan());

        this.billingState.refresh();
    }

    ngOnDestroy(): void {
        this.stopPolling$.next();
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** Esc closes the upgrade and Change plan pop-ups only; the blocking and confirming pop-ups cannot be closed. */
    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.view.mode === 'upgrade' || this.view.mode === 'changePlan') {
            this.close();
        }
    }

    onChangeSelection(selection: PlanSelection): void {
        this.changeSelection = selection.plan;
        this.renderChangePlan();
    }

    applyChangePlan(): void {
        if (this.changing || !this.changeSelection || this.changeView.buttonDisabled) {
            return;
        }
        this.changing = true;
        this.cd.markForCheck();
        this.api
            .changePlan(this.changeSelection)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: result => {
                    this.changing = false;
                    this.toastr.success(BillingGateComponent.changeMessageOf(result), null, AppConsts.toastrOption);
                    this.billingState.notifyPlanChanged();
                    this.setMode('none');
                },
                error: (error: unknown) => {
                    this.changing = false;
                    this.toastr.error(errorMessageOf(error, 'Your plan could not be changed. Please try again.'), null, AppConsts.toastrOption);
                    this.cd.markForCheck();
                },
            });
    }

    onSelection(selection: PlanSelection): void {
        this.selection = selection;
    }

    close(): void {
        this.setMode(this.status && this.status.isBlocked ? 'blocked' : 'none');
    }

    continueToPayment(): void {
        if (this.paying) {
            return;
        }
        this.paying = true;
        this.cd.markForCheck();
        this.api
            .createCheckoutSession(this.selection, this.billingState.isTeams)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: session => {
                    this.paying = false;
                    if (this.billingState.isTeams) {
                        // Checkout cannot run inside Teams: it opens in the browser and this tab waits for the webhook.
                        window.open(session.url, '_blank');
                        this.startConfirming(session.sessionId);
                    } else {
                        window.location.href = session.url;
                    }
                },
                error: (error: unknown) => {
                    this.paying = false;
                    this.toastr.error(errorMessageOf(error, 'We couldn’t open the payment page. Please try again.'), null, AppConsts.toastrOption);
                    this.cd.markForCheck();
                },
            });
    }

    updateCard(): void {
        if (this.openingPortal) {
            return;
        }
        this.openingPortal = true;
        this.cd.markForCheck();
        this.api
            .createBillingPortalSession()
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: url => {
                    this.openingPortal = false;
                    this.cd.markForCheck();
                    this.billingState.openStripePage(url);
                },
                error: () => {
                    this.openingPortal = false;
                    this.cd.markForCheck();
                },
            });
    }

    askAdmin(): void {
        if (this.asking || this.view.askSent) {
            return;
        }
        this.asking = true;
        this.cd.markForCheck();
        this.api
            .askAdminToUpgrade()
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.asking = false;
                    this.billingState.patch({ askAdminSentToday: true });
                },
                error: () => {
                    this.asking = false;
                    this.cd.markForCheck();
                },
            });
    }

    /** "Go to My day": a full load, so the session and every page pick up the paid plan. */
    goToMyDay(): void {
        // The site the user is on (a local build can point appBaseUrl at another environment).
        window.location.href = window.location.origin + '/app/main/my-day';
    }

    /** "This is taking longer than usual": the user keeps working; the background check finishes the job. */
    keepWorking(): void {
        this.setMode('none');
    }

    private openChangePlan(): void {
        if (this.view.mode !== 'none') {
            return;
        }
        this.api
            .getPlanChangeOptions()
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(options => {
                this.changeOptions = options;
                this.changeSelection = options.currentPlan;
                this.renderChangePlan();
                this.setMode('changePlan');
            });
    }

    private renderChangePlan(): void {
        const options = this.changeOptions;
        const view = BillingGateComponent.emptyChangeView();
        const nameOf = (plan: BillingPlanName | null): string => (plan === 'monthly' ? 'Monthly' : 'Yearly');
        const dateOf = (value: string | null): string =>
            value ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

        if (!options) {
            this.changeView = view;
            return;
        }
        if (!options.canChange) {
            view.reason = options.cannotChangeReason || 'Your plan can’t be changed right now.';
            view.buttonDisabled = true;
            this.changeView = view;
            this.cd.markForCheck();
            return;
        }

        view.currentLine = `You’re on the ${nameOf(options.currentPlan)} plan for ${options.people} ${options.people === 1 ? 'person' : 'people'}.`;
        view.pendingLine = options.pendingPlan
            ? `Switches to ${nameOf(options.pendingPlan)} on ${dateOf(options.pendingPlanDate)}.`
            : '';
        if (this.changeSelection === options.currentPlan) {
            view.buttonLabel = options.pendingPlan ? `Keep ${nameOf(options.currentPlan)}` : 'Current plan';
            view.buttonDisabled = !options.pendingPlan;
            view.note = options.pendingPlan ? `Cancels the switch to ${nameOf(options.pendingPlan)}.` : '';
        } else if (this.changeSelection === 'yearly') {
            view.buttonLabel = 'Switch to Yearly now';
            view.buttonDisabled = false;
            view.note = 'Takes effect today. Stripe credits the unused part of your month on the first yearly invoice.';
        } else {
            view.buttonLabel = `Switch to Monthly on ${dateOf(options.periodEnd)}`;
            view.buttonDisabled = false;
            view.note = `Your yearly plan runs until ${dateOf(options.periodEnd)}, then renews monthly.`;
        }
        this.changeView = view;
        this.cd.markForCheck();
    }

    private openUpgrade(): void {
        if (!this.status) {
            return;
        }
        if (this.status.isBlocked || !this.status.isBillingAdmin || this.view.mode !== 'none') {
            return;
        }
        this.setMode('upgrade');
    }

    private onStatus(status: BillingStatus): void {
        let mark: number | null;

        this.status = status;
        if (this.view.mode === 'confirming' || this.view.mode === 'done' || this.view.mode === 'slow') {
            this.render();
            return;
        }

        if (status.isBlocked) {
            this.setMode('blocked');
            return;
        }
        if (this.view.mode === 'blocked') {
            this.setMode('none');
            return;
        }

        // Story Part 3: admins see the upgrade pop-up by itself once at 3 days left and once at 1 day left.
        mark = this.dueUpgradeMark(status);
        if (mark !== null && this.view.mode === 'none') {
            this.api.markUpgradePromptShown(mark).pipe(take(1), takeUntil(this.destroy$)).subscribe();
            this.billingState.patch({ upgradePromptsShown: [...(status.upgradePromptsShown || []), mark] });
            this.setMode('upgrade');
            return;
        }
        this.render();
    }

    private dueUpgradeMark(status: BillingStatus): number | null {
        const shown = status.upgradePromptsShown || [];
        if (status.state !== 'trial' || !status.isBillingAdmin || status.trialDaysLeft === null) {
            return null;
        }
        if (status.trialDaysLeft <= 1 && shown.indexOf(1) < 0) {
            return 1;
        }
        if (status.trialDaysLeft <= 3 && status.trialDaysLeft > 1 && shown.indexOf(3) < 0) {
            return 3;
        }
        return null;
    }

    /** Back from Stripe Checkout (?checkout=success&session_id=…): confirm with the server, never trust the redirect. */
    private readCheckoutReturn(): void {
        const params = new URLSearchParams(window.location.search);
        const outcome = params.get('checkout');
        const sessionId = params.get('session_id');

        if (!outcome) {
            return;
        }
        params.delete('checkout');
        params.delete('session_id');
        this.location.replaceState(window.location.pathname, params.toString());
        if (outcome === 'success' && sessionId) {
            this.startConfirming(sessionId);
        }
    }

    private startConfirming(sessionId: string): void {
        const started = Date.now();

        this.confirmedCheckout = null;
        this.stopPolling$.next();
        this.setMode('confirming');

        timer(0, POLL_MS)
            .pipe(
                switchMap(() => this.api.getCheckoutStatus(sessionId)),
                takeUntil(this.stopPolling$),
                takeUntil(this.destroy$)
            )
            .subscribe(checkout => {
                if (checkout.confirmed) {
                    this.stopPolling$.next();
                    this.onConfirmed(checkout);
                } else if (Date.now() - started >= SLOW_AFTER_MS) {
                    this.stopPolling$.next();
                    this.setMode('slow');
                    this.pollInBackground(sessionId);
                }
            });
    }

    /** After the 30 seconds: keep checking quietly; when the webhook lands the app refreshes its billing state. */
    private pollInBackground(sessionId: string): void {
        const started = Date.now();

        timer(BACKGROUND_POLL_MS, BACKGROUND_POLL_MS)
            .pipe(
                switchMap(() => this.api.getCheckoutStatus(sessionId)),
                takeUntil(this.stopPolling$),
                takeUntil(this.destroy$)
            )
            .subscribe(checkout => {
                if (checkout.confirmed) {
                    this.stopPolling$.next();
                    if (this.view.mode === 'slow') {
                        this.onConfirmed(checkout);
                    } else {
                        this.billingState.refresh();
                    }
                } else if (Date.now() - started >= BACKGROUND_POLL_LIMIT_MS) {
                    this.stopPolling$.next();
                }
            });
    }

    private onConfirmed(checkout: CheckoutStatus): void {
        this.confirmedCheckout = checkout;
        this.setMode('done');
    }

    private setMode(mode: GateMode): void {
        this.view = { ...this.view, mode };
        this.render();
    }

    private render(): void {
        const status = this.status;
        const reason = this.reasonOf(status);
        const isAdmin = !!status && status.isBillingAdmin;
        const view: GateView = { ...BillingGateComponent.emptyView(), mode: this.view.mode };

        view.reason = reason;
        view.isAdmin = isAdmin;
        view.hasPrices = !!status && !!status.plans && status.plans.length === 2;
        if (status) {
            view.trialLine = status.trialDaysLeft === null
                ? ''
                : `${status.trialDaysLeft} ${status.trialDaysLeft === 1 ? 'day' : 'days'} left in your free trial.`;
            view.ownerName = status.ownerName || 'Your admin';
            view.ownerInitials = BillingGateComponent.initialsOf(status.ownerName);
            view.ownerPicture = status.ownerProfilePictureUrl || '';
            view.askSent = status.askAdminSentToday;
            view.askSentLabel = `Request sent to ${status.ownerFirstName || 'your admin'}`;
            view.cardLine = status.cardLast4
                ? `${BillingGateComponent.brandOf(status.cardBrand)} ending ${status.cardLast4}`
                : 'Your card';
            view.declinedLine = status.declinedOn
                ? `Declined on ${new Date(status.declinedOn).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                : 'Payment declined';
            view.dueLine = status.amountDue !== null ? `${formatMoney(status.amountDue, BillingGateComponent.currencyOf(status))} due` : '';
        }

        switch (reason) {
            case 'trialEnded':
                view.title = isAdmin ? 'Your free trial has ended' : 'Your firm’s free trial has ended';
                view.message = isAdmin
                    ? 'Your clients, cases/projects, and tasks are saved. Choose a plan to keep working in Trovve.'
                    : 'Your work is saved. Only an admin can choose a plan. We’ll let them know you want to keep using Trovve.';
                view.icon = 'clock';
                view.iconTone = 'amber';
                break;
            case 'paused':
                view.title = 'Your subscription is paused';
                view.message = isAdmin
                    ? 'We couldn’t take your last payment. Your work is saved. Update your card to keep working.'
                    : 'Your work is saved. Only an admin can update the payment details. We’ll let them know you want to keep using Trovve.';
                view.icon = 'credit-card';
                view.iconTone = 'red';
                break;
            case 'ended':
                view.title = 'Your subscription has ended';
                view.message = isAdmin
                    ? 'Your clients, cases/projects, and tasks are saved. Choose a plan to start again.'
                    : 'Your work is saved. Only an admin can choose a plan. We’ll let them know you want to keep using Trovve.';
                view.icon = 'calendar';
                view.iconTone = 'grey';
                break;
            default:
                break;
        }

        view.doneSummary = this.doneSummaryOf(this.confirmedCheckout, status);
        this.view = view;
        this.cd.markForCheck();
    }

    private reasonOf(status: BillingStatus | null): BlockReason | null {
        if (!status || !status.isBlocked) {
            return null;
        }
        switch (status.state) {
            case 'trialEnded':
                return 'trialEnded';
            case 'paused':
                return 'paused';
            case 'ended':
                return 'ended';
            case 'trial':
            case 'active':
            case 'paymentFailed':
            case 'cancelPending':
                return null;
            default:
                return null;
        }
    }

    private doneSummaryOf(checkout: CheckoutStatus | null, status: BillingStatus | null): string {
        let summary: string;
        let plan: string;
        let people: number;

        if (!checkout) {
            return '';
        }
        plan = checkout.plan === 'monthly' ? 'Monthly' : 'Yearly';
        people = checkout.people || 1;
        summary = `${plan} plan for ${people} ${people === 1 ? 'person' : 'people'}.`;
        if (checkout.nextPaymentAmount !== null && checkout.nextPaymentDate) {
            summary += ` Next payment: ${formatMoney(checkout.nextPaymentAmount, BillingGateComponent.currencyOf(status))} on `
                + new Date(checkout.nextPaymentDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + '.';
        }
        return summary + ' A receipt is on its way to your email.';
    }

    private static changeMessageOf(result: ChangePlanResult): string {
        switch (result.outcome) {
            case 'switchedNow':
                return 'You’re now on the Yearly plan.';
            case 'scheduled':
                return 'Your plan switches to Monthly when your current year ends.';
            case 'pendingCancelled':
                return 'You’ll stay on your current plan.';
            case 'unchanged':
                return 'Your plan is unchanged.';
            default:
                return 'Your plan is updated.';
        }
    }

    private static emptyChangeView(): ChangePlanView {
        return { currentLine: '', pendingLine: '', reason: '', buttonLabel: '', buttonDisabled: true, note: '' };
    }

    private static currencyOf(status: BillingStatus | null): string {
        return status && status.plans && status.plans.length ? status.plans[0].currency : 'usd';
    }

    private static brandOf(brand: string | null): string {
        if (!brand) {
            return 'Card';
        }
        return brand === 'amex' ? 'American Express' : brand.charAt(0).toUpperCase() + brand.slice(1);
    }

    private static initialsOf(name: string | null): string {
        const parts = (name || '').trim().split(/\s+/).filter(part => part.length > 0);
        if (parts.length === 0) {
            return '?';
        }
        return (parts[0].charAt(0) + (parts.length > 1 ? parts[parts.length - 1].charAt(0) : '')).toUpperCase();
    }

    private static emptyView(): GateView {
        return {
            mode: 'none',
            reason: null,
            isAdmin: false,
            hasPrices: false,
            title: '',
            message: '',
            icon: 'clock',
            iconTone: 'amber',
            trialLine: '',
            cardLine: '',
            declinedLine: '',
            dueLine: '',
            ownerName: '',
            ownerInitials: '',
            ownerPicture: '',
            askSent: false,
            askSentLabel: '',
            doneSummary: '',
        };
    }
}
